/**
 * Sales Orders Redux Slice
 * State + reducers for sales-orders domain
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  SalesOrder,
  SalesOrderStats,
  SalesOrdersFilter,
  SalesOrdersSortBy,
  SalesOrdersSortOrder,
  SalesOrderSkuOption,
  SalesOrderStatus,
  CreateSalesOrderRequest,
  PaginationMeta,
  PaginatedSalesOrders,
} from '../domain/types'
import { DEFAULT_PAGE_SIZE, DEFAULT_SALES_ORDER_SORT } from '../domain/constants'
import { DEFAULT_SALES_ORDERS_FILTER } from '../domain/filter-config'

export interface SalesOrdersState {
  items: SalesOrder[]
  selectedSalesOrder: SalesOrder | null
  stats: SalesOrderStats
  filter: SalesOrdersFilter
  pagination: PaginationMeta
  skuOptions: SalesOrderSkuOption[]
  skuOptionsLoading: boolean
  skuOptionsError: string | null; skuOptionsLoaded: boolean
  loading: boolean
  error: string | null
}

const initialState: SalesOrdersState = {
  items: [],
  selectedSalesOrder: null,
  stats: {
    active: 0,
    completed: 0,
    draft: 0,
    total: 0,
    totalValue: { amount: 0, currency: 'SAR' },
  },
  filter: { ...DEFAULT_SALES_ORDERS_FILTER },
  pagination: {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    pageCount: 1,
    total: 0,
  },
  skuOptions: [],
  skuOptionsLoading: false,
  skuOptionsError: null, skuOptionsLoaded: false,
  loading: false,
  error: null,
}

export const salesOrdersSlice = createSlice({
  name: 'salesOrders',
  initialState,
  reducers: {
    // Data setters (used by loaders)
    setSalesOrders: (state, action: PayloadAction<SalesOrder[]>) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    },
    setSelectedSalesOrder: (state, action: PayloadAction<SalesOrder | null>) => {
      state.selectedSalesOrder = action.payload
    },
    setStats: (state, action: PayloadAction<SalesOrderStats>) => {
      state.stats = action.payload
    },
    setFilter: (state, action: PayloadAction<SalesOrdersFilter>) => {
      state.filter = action.payload
      // Keep pagination.page in sync so PaginationControls reflects the correct
      // current page immediately (before the API response arrives).
      if (action.payload.page !== undefined) {
        state.pagination.page = action.payload.page
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },

    // Action triggers (handled by sagas)
    fetchSalesOrders: (state, _action: PayloadAction<SalesOrdersFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchSalesOrdersSuccess: (state, action: PayloadAction<PaginatedSalesOrders>) => {
      state.items = action.payload.data
      state.pagination = action.payload.pagination
      state.loading = false
    },
    fetchSalesOrdersFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },
    fetchSalesOrderStats: () => {
      // Keep stats refresh as a background operation.
    },
    fetchSalesOrderStatsSuccess: (state, action: PayloadAction<SalesOrderStats>) => {
      state.stats = action.payload
    },
    fetchSalesOrderStatsFailure: () => {
      // Silent fail for stats refresh.
    },
    fetchSkuOptions: (state) => {
      state.skuOptionsLoading = true
      state.skuOptionsError = null
    },
    fetchSkuOptionsSuccess: (state, action: PayloadAction<SalesOrderSkuOption[]>) => {
      state.skuOptions = action.payload; state.skuOptionsLoaded = true
      state.skuOptionsLoading = false
    },
    fetchSkuOptionsFailure: (state, action: PayloadAction<string>) => {
      state.skuOptionsError = action.payload; state.skuOptionsLoaded = true
      state.skuOptionsLoading = false
    },

    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.filter.page = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter.pageSize = action.payload
      state.filter.page = 1 // Reset to first page when page size changes
    },
    goToNextPage: (state) => {
      if (state.pagination.page < state.pagination.pageCount) {
        state.filter.page = (state.filter.page ?? 1) + 1
      }
    },
    goToPreviousPage: (state) => {
      if ((state.filter.page ?? 1) > 1) {
        state.filter.page = (state.filter.page ?? 1) - 1
      }
    },

    createSalesOrder: (state, _action: PayloadAction<CreateSalesOrderRequest>) => {
      state.loading = true
      state.error = null
    },
    createSalesOrderSuccess: (state, action: PayloadAction<SalesOrder>) => {
      state.items.unshift(action.payload)
      state.stats.draft += 1
      state.stats.total += 1
      state.loading = false
    },
    createSalesOrderFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    renewSalesOrder: (state, _action: PayloadAction<{ salesOrderId: string; newEndDate: string }>) => {
      state.loading = true
      state.error = null
    },
    renewSalesOrderSuccess: (state, action: PayloadAction<SalesOrder>) => {
      const index = state.items.findIndex(c => c.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      state.loading = false
    },
    renewSalesOrderFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Update sales order
    updateSalesOrder: (state, _action: PayloadAction<{ id: string } & Partial<CreateSalesOrderRequest>>) => {
      state.loading = true
      state.error = null
    },
    updateSalesOrderSuccess: (state, action: PayloadAction<SalesOrder>) => {
      const index = state.items.findIndex(
        c => c.id === action.payload.id || c.orderNumber === action.payload.orderNumber
      )
      if (index !== -1) {
        state.items[index] = action.payload
      }
      state.loading = false
    },
    updateSalesOrderFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Delete sales order
    deleteSalesOrder: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    deleteSalesOrderSuccess: (state, action: PayloadAction<string>) => {
      const salesOrder = state.items.find(c => c.id === action.payload)
      if (salesOrder) {
        // Update stats
        if (salesOrder.status === 'active') state.stats.active -= 1
        else if (salesOrder.status === 'completed') state.stats.completed -= 1
        else if (salesOrder.status === 'draft') state.stats.draft -= 1
        state.stats.total -= 1
        state.stats.totalValue.amount -= salesOrder.totalValue.amount
      }
      state.items = state.items.filter(c => c.id !== action.payload)
      state.loading = false
    },
    deleteSalesOrderFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Cancel sales order
    cancelSalesOrder: (state, _action: PayloadAction<{ salesOrderId: string }>) => {
      state.error = null
    },
    cancelSalesOrderSuccess: (state, action: PayloadAction<SalesOrder>) => {
      const index = state.items.findIndex(c => c.id === action.payload.id)
      if (index !== -1) {
        if (state.selectedSalesOrder?.id === action.payload.id) {
          state.selectedSalesOrder = action.payload
        }
        const previousStatus = state.items[index].status
        state.items[index] = action.payload

        if (previousStatus === 'active') state.stats.active -= 1
        else if (previousStatus === 'completed') state.stats.completed -= 1
        else if (previousStatus === 'draft') state.stats.draft -= 1
      }
      state.loading = false
    },
    cancelSalesOrderFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Duplicate sales order
    duplicateSalesOrder: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    duplicateSalesOrderSuccess: (state, action: PayloadAction<SalesOrder>) => {
      state.items.unshift(action.payload)
      state.stats.draft += 1
      state.stats.total += 1
      state.stats.totalValue.amount += action.payload.totalValue.amount
      state.loading = false
    },
    duplicateSalesOrderFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Search/filter
    searchSalesOrders: (state, action: PayloadAction<string>) => {
      const search = action.payload.trim()
      state.filter = {
        ...state.filter,
        search: search || undefined,
        page: 1,
      }
    },
    applySearchFilters: (
      state,
      action: PayloadAction<{
        search?: string
        productCodes?: string[]
        deliveryAddresses?: string[]
        factoryIds?: string[]
        minValue?: number
        maxValue?: number
      }>
    ) => {
      state.filter = {
        ...state.filter,
        search: action.payload.search,
        productCodes: action.payload.productCodes ?? state.filter.productCodes,
        deliveryAddresses: action.payload.deliveryAddresses ?? state.filter.deliveryAddresses,
        factoryIds: action.payload.factoryIds ?? state.filter.factoryIds,
        minValue: action.payload.minValue ?? state.filter.minValue,
        maxValue: action.payload.maxValue ?? state.filter.maxValue,
        page: 1,
      }
    },
    filterByStatus: (state, action: PayloadAction<SalesOrderStatus | SalesOrderStatus[] | 'all' | undefined>) => {
      const payload = action.payload
      const statusList = Array.isArray(payload)
        ? payload
        : payload && payload !== 'all'
          ? [payload]
          : []
      state.filter = { ...state.filter, status: statusList }
    },
    filterByType: (state, action: PayloadAction<SalesOrdersFilter['type']>) => {
      state.filter = { ...state.filter, type: action.payload }
    },
    filterByFactory: (state, action: PayloadAction<string[] | undefined>) => {
      state.filter = { ...state.filter, factoryIds: action.payload ?? [], page: 1 }
    },
    setSort: (state, action: PayloadAction<{ sortBy: SalesOrdersSortBy; sortOrder: SalesOrdersSortOrder }>) => {
      state.filter = {
        ...state.filter,
        sortBy: action.payload.sortBy,
        sortOrder: action.payload.sortOrder,
        page: 1,
      }
    },

    // Clear state (also sets loading=true so spinner shows immediately on language change)
    clearSalesOrders: (state) => {
      state.items = []
      state.selectedSalesOrder = null
      state.error = null
      state.loading = true
    },
    clearError: (state) => {
      state.error = null
    },
  },
})

export const salesOrdersActions = salesOrdersSlice.actions
export const salesOrdersReducer = salesOrdersSlice.reducer

// Legacy aliases for backwards compatibility
export const contractsActions = salesOrdersActions
export const contractsReducer = salesOrdersReducer
export type ContractsState = SalesOrdersState
